import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
/* eslint-disable react/react-in-jsx-scope */
import IconExcel from "@/assets/icones/excel.svg?react";
import IconImg from "@/assets/icones/images.svg?react";
import IconOds from "@/assets/icones/ods.svg?react";
import IconPdf from "@/assets/icones/pdf.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import { useAuth } from "@/auth/hooks/useAuth";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTabDashboard } from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { TreeRoot } from "@/types";
import { Download, FolderOpen, Info, Link, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import usePostData from "@/hooks/usePostData";
import { copyDocumentLink } from "@/documents/utils/copyDocumentLink";
import { downloadDocumentFile } from "@/documents/utils/downloadDocumentFile";
import {
  showDownloadErrorToast,
  showDownloadInProgressToast,
  showDownloadSuccessToast,
} from "@/documents/utils/downloadToast";
import { getDocumentCategoryInfo } from "@/documents/utils/documentTree";
import { toast } from "@/components/ui/use-toast";
import DocumentDetailsDialog from "./DocumentDetailsDialog";

const formatDocumentDate = (
  value?: string | { date?: string } | null,
  precision?: string | null,
) => {
  const rawDate = typeof value === "string" ? value : value?.date;
  if (!rawDate) return "";

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleDateString(
    "fr-FR",
    precision === "month"
      ? {
          month: "long",
          year: "numeric",
        }
      : {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        },
  );
};

const DocumentList = ({
  data,
  typesDocs,
  onDelete,
  onShowDetails,
}: {
  data: any[];
  typesDocs: any[];
  onDelete?: (id: number) => void | Promise<void>;
  onShowDetails?: (doc: any) => void;
}) => {
  const { user } = useAuth();
  const hidePostActions = useHidePostActions();
  const { tab } = useTabDashboard();
  const [hasAccessToEdit, setHasAccessToEdit] = useState(true);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState("chronologie");
  const [detailsDocument, setDetailsDocument] = useState<any | null>(null);

  useEffect(() => {
    if (user) {
      setHasAccessToEdit(
        Boolean(
          user?.roles?.includes("ROLE_CARRIER") ||
          user?.roles?.includes("ROLE_CPINTERVENANT") ||
          (user?.roles?.includes("ROLE_SUBCONTRACTOR") && user?.fullAccess),
        ),
      );
    }
  }, [user]);

  const { data: treeFinancer, loading: loadingTreeFinancer } = useFetchDataV2<
    TreeRoot[]
  >(
    `${import.meta.env.VITE_API_ENDPOINT}/tree/carrier`,
    `tree-carrier`,
    1000 * 60 * 60,
  );
  const { postData } = usePostData<any>();
  const handleDelete = async (id: number) => {
    if (id) {
      if (onDelete) {
        await onDelete(Number(id));
        return;
      }

      const res = await postData({
        url: `${import.meta.env.VITE_IP_API}/api/media/delete/${id}`,
        data: {},
      });
      void res;
    }
  };
  const handleCopyLink = async (url: string) => {
    try {
      await copyDocumentLink(url);
      toast({ title: "Lien copié" });
    } catch {
      toast({ title: "Impossible de copier le lien", variant: "destructive" });
    }
  };
  const handleDownload = async (url: string, filename: string) => {
    const downloadToast = showDownloadInProgressToast({
      filename,
      kind: "file",
    });

    try {
      await downloadDocumentFile(url, filename);
      downloadToast.dismiss();
      showDownloadSuccessToast({ filename, kind: "file" });
    } catch {
      downloadToast.dismiss();
      showDownloadErrorToast({ filename, kind: "file" });
    }
  };
  const handleShowDetails = (doc: any) => {
    if (onShowDetails) {
      onShowDetails(doc);
      return;
    }

    setDetailsDocument(doc);
  };
  const normalizedDocuments = useMemo(
    () =>
      data?.map((doc: any) => {
        const categoryInfo = getDocumentCategoryInfo(typesDocs, doc);

        return {
          ...doc,
          ...categoryInfo,
        };
      }) ?? [],
    [data, typesDocs],
  );

  const groupedDocuments = useMemo(() => {
    const groups = new Map<string, { key: string; label: string; date: Date; docs: any[] }>();

    normalizedDocuments.forEach((doc) => {
      if (groupBy === "categorie") {
        const categoryKey = doc.category || "Autres";
        const existing = groups.get(categoryKey);
        if (existing) {
          existing.docs.push(doc);
        } else {
          groups.set(categoryKey, {
            key: categoryKey,
            label: categoryKey,
            date: new Date(0),
            docs: [doc],
          });
        }
        return;
      }

      const rawDate =
        (typeof doc?.periodAt === "string" ? doc.periodAt : doc?.periodAt?.date) ||
        doc?.at?.date ||
        doc?.createdAt;
      const parsed = rawDate ? new Date(rawDate) : null;

      if (!parsed || Number.isNaN(parsed.getTime())) {
        const undated = groups.get("non-date") || {
          key: "non-date",
          label: "non daté",
          date: new Date(0),
          docs: [],
        };
        undated.docs.push(doc);
        groups.set("non-date", undated);
        return;
      }

      const key = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
      const existing = groups.get(key);
      if (existing) {
        existing.docs.push(doc);
      } else {
        groups.set(key, {
          key,
          label: parsed.toLocaleDateString("fr-FR", {
            month: "long",
            year: "numeric",
          }),
          date: new Date(parsed.getFullYear(), parsed.getMonth(), 1),
          docs: [doc],
        });
      }
    });

    return [...groups.values()].sort((a, b) =>
      groupBy === "categorie"
        ? a.label.localeCompare(b.label, "fr")
        : b.date.getTime() - a.date.getTime(),
    );
  }, [groupBy, normalizedDocuments]);

  useEffect(() => {
    const groupKeys = groupedDocuments.map((group) => group.key);
    setOpenItems((prev) => {
      if (prev.length === 0) {
        return groupKeys;
      }

      const previousKeys = new Set(prev);
      const nextOpenItems = groupKeys.filter((key) => previousKeys.has(key));

      return nextOpenItems;
    });
  }, [groupedDocuments]);

  if (loadingTreeFinancer) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-40 border-0 px-4 font-normal text-foreground/90">
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="categorie">Sous-catégorie</SelectItem>
              <SelectItem value="chronologie">Chronologie</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <Accordion
        type="multiple"
        className="w-full"
        value={openItems}
        onValueChange={setOpenItems}
      >
        {groupedDocuments.map((group) => (
          <AccordionItem value={group.key} className="border-0 mb-4" key={group.key}>
            <AccordionTrigger className="py-[10px] hover:no-underline group">
              <div className="flex justify-between items-start w-full bg-[#FFFF] px-[18px] py-[10px] rounded-[9px]">
                <div className="flex items-center gap-2 flex-1 text-left pr-4">
                  <FolderOpen className="text-gray-950" />
                  <div className="flex-1 group-hover:underline text-gray-900">{group.label}</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="bg-white -mt-4 pt-8 px-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Catégorie</TableHead>
                    {(!tab || tab != "document") && (
                      <>
                        <TableHead>Programmes</TableHead>
                        <TableHead>Projets</TableHead>
                      </>
                    )}
                    <TableHead>Date d'ajout</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.docs?.map((pj, key) => {
                    const fileName = pj?.name?.toLowerCase() ?? "";
                    const extension = fileName === "blob"
                      ? "image"
                      : pj?.name?.split(".").pop()?.toLowerCase() || "pdf";
                    const fileUrl = `${import.meta.env.VITE_IP_API}/media/${pj.path}`;
                    const downloadUrl = `${import.meta.env.VITE_API_ENDPOINT}/media/download/${pj.path}`;
                    const pp = treeFinancer?.find((t) => t.id === Number(pj.pp));
                    const program = pp?.programs.find((p) => p.id === Number(pj.program));
                    const project = program?.projects.find((p) => p.id === Number(pj.project));

                    return (
                      <TableRow
                        key={`${pj.id}-${key}`}
                        className={`${key % 2 == 0 ? "bg-gray-50" : "bg-white"}`}
                      >
                        <TableCell className="font-medium flex items-center gap-x-2 align-top w-80">
                          <TypeFile type={extension} />
                          <span className="truncate">{pj.name}</span>
                        </TableCell>
                        <TableCell className="align-top w-40">
                          {pj.subcategory ?? pj.category ?? "Autres"}
                        </TableCell>
                        {(!tab || tab != "document") && (
                          <>
                            <TableCell className="align-top">{program?.name}</TableCell>
                            <TableCell className="align-top">{project?.name}</TableCell>
                          </>
                        )}

                        <TableCell className="align-top">
                          {formatDocumentDate(pj.at ?? pj.createdAt, pj.datePrecision)}
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Voir les détails"
                              aria-label="Voir les détails"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleShowDetails(pj);
                              }}
                            >
                              <Info width={22} height={22} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Télécharger"
                              aria-label="Télécharger"
                              className="flex gap-2 items-center cursor-pointer text-foreground"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDownload(downloadUrl, pj.name);
                              }}
                            >
                              <Download width={22} height={22} />
                            </Button>
                            <Button
                              variant={"ghost"}
                              size={"icon"}
                              title="Copier le lien"
                              aria-label="Copier le lien"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCopyLink(fileUrl);
                              }}
                            >
                              <Link width={22} height={22} />
                            </Button>

                            {hasAccessToEdit && !hidePostActions && (
                              <Button
                                variant={"ghost"}
                                size={"icon"}
                                className="hover:bg-red-500 hover:text-white text-red-500"
                                onClick={() => handleDelete(pj.id)}
                              >
                                <Trash2 width={22} height={22} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <DocumentDetailsDialog
        open={Boolean(detailsDocument)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailsDocument(null);
          }
        }}
        pj={detailsDocument}
        treeFinancer={treeFinancer}
      />
    </>
  );
};

const TypeFile = ({ type }: { type: string }) => {
  const icon = {
    pdf: <IconPdf width={22} height={22} />,
    doc: <IconWord width={22} height={22} />,
    docx: <IconWord width={22} height={22} />,
    xlsx: <IconExcel width={22} height={22} />,
    ods: <IconOds width={22} height={22} />,
    jpg: <IconImg width={22} height={22} />,
    jpeg: <IconImg width={22} height={22} />,
    png: <IconImg width={22} height={22} />,
    gif: <IconImg width={22} height={22} />,
    bmp: <IconImg width={22} height={22} />,
    webp: <IconImg width={22} height={22} />,
    image: <IconImg width={22} height={22} />,
  } as Record<string, JSX.Element>;

  return <div>{icon[type] ?? <IconPdf width={22} height={22} />}</div>;
};

export default DocumentList;
